import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import PropertyMaintenance from '../maintenance/PropertyMaintenance';
import PropertyDocumentsSection from '../../components/PropertyDocumentsSection';
import PropertyLeasesSection from '../../components/PropertyLeasesSection';
import QuickPricingModal from '../pricing/QuickPricingModal';

function PropertyEditPage() {
  const navigate = useNavigate();
  const { propertyId } = useParams();
  const [searchParams] = useSearchParams();
  const [property, setProperty] = useState(null);
  const [propertyAttributes, setPropertyAttributes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddAttributeModal, setShowAddAttributeModal] = useState(false);
  const [newAttribute, setNewAttribute] = useState('');
  const [newAttributeType, setNewAttributeType] = useState('string');
  const [attributeTypes, setAttributeTypes] = useState({});
  
  // Unit type management state
  const [unitTypes, setUnitTypes] = useState([]);
  const [showAddUnitTypeModal, setShowAddUnitTypeModal] = useState(false);
  const [editingUnitType, setEditingUnitType] = useState(null);
  const [unitAllocation, setUnitAllocation] = useState({
    property_total_units: 0,
    total_allocated_units: 0,
    available_units: 0
  });
  const [newUnitType, setNewUnitType] = useState({
    type_name: '',
    bedroom_count: 1,
    bathroom_count: 1,
    unit_count: 1,
    square_footage: '',
    minimum_expected_rent: '',
    floor_rent_premium: '',
    description: ''
  });
  
  // Units management state
  const [units, setUnits] = useState([]);
  const [showAddUnitModal, setShowAddUnitModal] = useState(false);
  const [showBulkUnitModal, setShowBulkUnitModal] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [newUnit, setNewUnit] = useState({
    unit_number: '',
    unit_type_id: '',
    status: 'vacant',
    square_footage: '',
    bedrooms: '',
    bathrooms: '',
    floor_number: '',
    monthly_rent: null,
    notes: ''
  });
  const [bulkUnitSchemes, setBulkUnitSchemes] = useState([{
    prefix: '',
    start_number: 1,
    end_number: 1,
    unit_type_id: '',
    status: 'vacant',
    square_footage: '',
    bedrooms: '',
    bathrooms: '',
    monthly_rent: null  // Units start with null rent until priced through engine
  }]);

  // Units pagination state
  const [unitPage, setUnitPage] = useState(1);
  const unitsPerPage = 30;

  // Notes state
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');

  // Operational metrics state
  const [dashboardMetrics, setDashboardMetrics] = useState({
    scheduledRent: 0,
    collectedRent: 0,
    outstandingRent: 0,
    assistanceReceived: 0,
    expenses: 0,
    netOperatingIncome: 0,
    occupancyRate: 0,
    collectionRate: 0
  });
  const [alerts, setAlerts] = useState([]);
  const [assistanceBatches, setAssistanceBatches] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [maintenanceTickets, setMaintenanceTickets] = useState([]);
  const [leases, setLeases] = useState([]);

  // Tab navigation state - check URL param for tab
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');

  // Pricing modal state
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [pricingModalContext, setPricingModalContext] = useState({
    unitTypeId: null,
    unitId: null,
    unitData: null
  });

  useEffect(() => {
    const storedUser = localStorage.getItem('username');
    if (!storedUser) {
      navigate('/');
      return;
    }
    
    fetchPropertyData();
    fetchAttributeTypes();
    fetchUnitTypes();
    fetchUnitAllocation();
    fetchUnits();
    fetchNotes();
    fetchDashboardMetrics();
    fetchAlerts();
  }, [propertyId, navigate]);

  useEffect(() => {
    if (activeTab === 'documents') {
      fetchDocuments();
    }
    if (activeTab === 'leases') {
      fetchLeases();
    }
  }, [activeTab, propertyId]);

  const fetchDocuments = async () => {
    if (!propertyId) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/properties/${propertyId}/documents`);
      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      }
    } catch (err) {
      console.error('Error fetching documents:', err);
    }
  };

  const fetchLeases = async () => {
    if (!propertyId) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/properties/${propertyId}/leases?status=active`);
      if (response.ok) {
        const data = await response.json();
        setLeases(data);
      }
    } catch (err) {
      console.error('Error fetching leases:', err);
    }
  };

  const fetchAlerts = async () => {
    if (!propertyId) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/alerts?entity_type=property&entity_id=${propertyId}`);
      if (response.ok) {
        const data = await response.json();
        setAlerts(data);
      }
    } catch (err) {
      console.error('Error fetching alerts:', err);
    }
  };

  const fetchDashboardMetrics = async () => {
    if (!propertyId) return;
    
    try {
      // Get current month dates
      const now = new Date();
      const startDate = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
      const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];
      
      // Fetch property detail metrics
      const response = await fetch(`${API_BASE_URL}/api/portfolio/property/${propertyId}?start_date=${startDate}&end_date=${endDate}`);
      if (response.ok) {
        const data = await response.json();
        const stats = data.stats || {};
        
        // Calculate occupancy rate
        const occupancyRate = stats.unit_count > 0 
          ? ((stats.occupied_units / stats.unit_count) * 100).toFixed(1)
          : 0;
        
        // Calculate collection rate
        const collectionRate = stats.rent_due > 0
          ? ((stats.rent_collected / stats.rent_due) * 100).toFixed(1)
          : 0;
        
        setDashboardMetrics({
          scheduledRent: stats.rent_due || 0,
          collectedRent: stats.rent_collected || 0,
          outstandingRent: (stats.rent_due || 0) - (stats.rent_collected || 0),
          assistanceReceived: 0, // TODO: Add assistance tracking
          expenses: stats.total_expenses || 0,
          netOperatingIncome: (stats.rent_collected || 0) - (stats.total_expenses || 0),
          occupancyRate: parseFloat(occupancyRate),
          collectionRate: parseFloat(collectionRate)
        });
      }
      
      // Fetch expenses for this month
      const expensesResponse = await fetch(`${API_BASE_URL}/api/financial/expenses?property_id=${propertyId}&start_date=${startDate}&end_date=${endDate}`);
      if (expensesResponse.ok) {
        const expensesData = await expensesResponse.json();
        const totalExpenses = expensesData.reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);
        setDashboardMetrics(prev => ({
          ...prev,
          expenses: totalExpenses,
          netOperatingIncome: prev.collectedRent - totalExpenses
        }));
      }
    } catch (err) {
      console.error('Error fetching dashboard metrics:', err);
    }
  };

  // Helper function to format address for display
  const formatAddressForDisplay = (property) => {
    // If we have separate fields, combine them
    const street = (property['Street Address'] || '').trim();
    const city = (property['City'] || '').trim();
    const state = (property['State'] || '').trim();
    const zip = (property['Zip Code'] || '').trim();
    
    if (street || city || state || zip) {
      const parts = [];
      if (street) parts.push(street);
      if (city) parts.push(city);
      if (state && zip) {
        parts.push(`${state} ${zip}`);
      } else if (state) {
        parts.push(state);
      } else if (zip) {
        parts.push(zip);
      }
      return parts.join(', ');
    }
    
    // Fallback to Address field if it exists (backwards compatibility)
    return property['Address'] || '';
  };

  // Helper function to parse address string into components
  const parseAddress = (addressString) => {
    if (!addressString) {
      return { street: '', city: '', state: '', zip: '' };
    }

    // Try to parse format: "Street, City, State zip"
    const parts = addressString.split(',').map(p => p.trim());
    
    if (parts.length >= 3) {
      // Format: "Street, City, State zip"
      const street = parts[0];
      const city = parts[1];
      const stateZip = parts[2].split(/\s+/);
      const state = stateZip[0] || '';
      const zip = stateZip.slice(1).join(' ') || '';
      return { street, city, state, zip };
    } else if (parts.length === 2) {
      // Format: "Street, City" or "Street, State zip"
      return { street: parts[0], city: parts[1] || '', state: '', zip: '' };
    } else {
      // Just street address
      return { street: addressString, city: '', state: '', zip: '' };
    }
  };

  const fetchPropertyData = async () => {
    try {
      // Use the new individual property endpoint
      const response = await fetch(`${API_BASE_URL}/api/properties/${propertyId}`);
      if (!response.ok) {
        if (response.status === 404) {
          setError('Property not found');
          return;
        }
        throw new Error('Failed to fetch property');
      }
      
      const currentProperty = await response.json();
      // Transform backend snake_case keys to the Title Case keys used by the UI
      const transformKey = (key) => key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
      const transformed = { id: currentProperty.id };
      Object.keys(currentProperty).forEach((key) => {
        if (key === 'id') return;
        let displayKey;
        switch (key) {
          case 'property_name': displayKey = 'Property Name'; break;
          case 'address': displayKey = 'Address'; break;
          case 'unit_count': displayKey = 'Unit Count'; break;
          case 'property_manager': displayKey = 'Property Manager'; break;
          case 'property_type': displayKey = 'Property Type'; break;
          case 'year_built': displayKey = 'Year Built'; break;
          case 'total_square_footage': displayKey = 'Total Square Footage'; break;
          case 'property_status': displayKey = 'Property Status'; break;
          case 'purchase_price': displayKey = 'Purchase Price'; break;
          case 'current_market_value': displayKey = 'Current Market Value'; break;
          case 'monthly_revenue': displayKey = 'Monthly Revenue'; break;
          case 'monthly_expenses': displayKey = 'Monthly Expenses'; break;
          case 'maintenance_contact': displayKey = 'Maintenance Contact'; break;
          case 'maintenance_phone': displayKey = 'Maintenance Phone'; break;
          case 'last_inspection_date': displayKey = 'Last Inspection Date'; break;
          case 'next_inspection_date': displayKey = 'Next Inspection Date'; break;
          default:
            // Custom attributes: convert to Title Case
            displayKey = transformKey(key);
        }
        transformed[displayKey] = currentProperty[key];
      });
      
      // Parse address into separate fields
      if (transformed['Address']) {
        const parsed = parseAddress(transformed['Address']);
        transformed['Street Address'] = parsed.street;
        transformed['City'] = parsed.city;
        transformed['State'] = parsed.state;
        transformed['Zip Code'] = parsed.zip;
      } else {
        transformed['Street Address'] = '';
        transformed['City'] = '';
        transformed['State'] = '';
        transformed['Zip Code'] = '';
      }
      
      setProperty(transformed);
      
      // Fetch attributes
      const attrResponse = await fetch(`${API_BASE_URL}/api/properties/attributes`);
      if (attrResponse.ok) {
        const attrData = await attrResponse.json();
        setPropertyAttributes(attrData.attributes || attrData);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAttributeTypes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/properties/attribute-types`);
      if (response.ok) {
        const data = await response.json();
        setAttributeTypes(data.attribute_types || data);
      }
    } catch (err) {
      console.error('Error fetching attribute types:', err);
    }
  };

  const fetchUnitTypes = async () => {
    if (!propertyId) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/properties/${propertyId}/unit-types`);
      if (response.ok) {
        const data = await response.json();
        setUnitTypes(data);
      }
    } catch (err) {
      console.error('Error fetching unit types:', err);
    }
  };

  const fetchUnitAllocation = async () => {
    if (!propertyId) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/properties/${propertyId}/unit-summary`);
      if (response.ok) {
        const data = await response.json();
        setUnitAllocation({
          property_total_units: data.property_total_units,
          total_allocated_units: data.total_allocated_units,
          available_units: data.available_units
        });
      }
    } catch (err) {
      console.error('Error fetching unit allocation:', err);
    }
  };

  const fetchUnits = async () => {
    if (!propertyId) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/properties/${propertyId}/units`);
      if (response.ok) {
        const data = await response.json();
        setUnits(data);
        // Update property unit_count to reflect actual units
        if (property && data.length !== property['Unit Count']) {
          setProperty({...property, 'Unit Count': data.length});
        }
      }
    } catch (err) {
      console.error('Error fetching units:', err);
    }
  };

  const fetchNotes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/properties/${propertyId}/notes`);
      if (response.ok) {
        const data = await response.json();
        setNotes(data.notes || []);
      }
    } catch (err) {
      // ignore
    }
  };

  const handleAddNote = async () => {
    const text = (newNote || '').trim();
    if (!text) return;
    try {
      const createdBy = localStorage.getItem('username') || '';
      const response = await fetch(`${API_BASE_URL}/api/properties/${propertyId}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note_text: text, created_by: createdBy })
      });
      if (response.ok) {
        const data = await response.json();
        setNotes(prev => [data.note, ...prev]);
        setNewNote('');
      } else {
        const errData = await response.json();
        setError(errData.error || 'Failed to add note');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAddUnitType = async () => {
    if (!propertyId) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/properties/${propertyId}/unit-types`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newUnitType)
      });
      
      if (response.ok) {
        const data = await response.json();
        setShowAddUnitTypeModal(false);
        
        // Prepare bulk config with data from the unit type to force creation of actual units
        setBulkUnitSchemes([{
          prefix: '',
          start_number: 1,
          end_number: newUnitType.unit_count || 1,
          unit_type_id: data.id,
          status: 'vacant',
          square_footage: newUnitType.square_footage,
          bedrooms: newUnitType.bedroom_count,
          bathrooms: newUnitType.bathroom_count,
          monthly_rent: null  // Units start with null rent until priced through engine
        }]);

        setNewUnitType({
          type_name: '',
          bedroom_count: 1,
          bathroom_count: 1,
          unit_count: 1,
          square_footage: '',
          minimum_expected_rent: '',
          floor_rent_premium: '',
          description: ''
        });
        
        fetchUnitTypes();
        fetchUnitAllocation();
        
        // Force open the bulk creation modal
        setShowBulkUnitModal(true);
        
        setSuccess('Unit type added! Please specify unit numbers for bulk creation.');
        setTimeout(() => setSuccess(''), 5000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to add unit type');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateUnitType = async () => {
    if (!editingUnitType) return;
    
    try {
      // Send the newUnitType (which contains the edited values), not editingUnitType (which is the original)
      const response = await fetch(`${API_BASE_URL}/api/unit-types/${editingUnitType.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newUnitType)
      });
      
      if (response.ok) {
        setShowAddUnitTypeModal(false);
        setEditingUnitType(null);
        // Reset form to default values
        setNewUnitType({
          type_name: '',
          bedroom_count: 1,
          bathroom_count: 1,
          unit_count: 1,
          square_footage: '',
          minimum_expected_rent: '',
          floor_rent_premium: '',
          description: ''
        });
        fetchUnitTypes();
        fetchUnitAllocation();
        setSuccess('Unit type updated successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update unit type');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteUnitType = async (unitTypeId) => {
    if (!window.confirm('Are you sure you want to delete this unit type?')) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/unit-types/${unitTypeId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        fetchUnitTypes();
        fetchUnitAllocation();
        setSuccess('Unit type deleted successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to delete unit type');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const openEditUnitTypeModal = (unitType) => {
    setEditingUnitType(unitType);
    setNewUnitType({
      type_name: unitType.type_name,
      bedroom_count: unitType.bedroom_count,
      bathroom_count: unitType.bathroom_count,
      unit_count: unitType.unit_count,
      square_footage: unitType.square_footage || '',
      minimum_expected_rent: unitType.minimum_expected_rent ?? '',
      floor_rent_premium: unitType.floor_rent_premium ?? '',
      description: unitType.description || ''
    });
    setShowAddUnitTypeModal(true);
  };

  // Unit management functions
  const handleCreateUnit = async () => {
    if (!newUnit.unit_number.trim()) {
      setError('Unit number is required');
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/properties/${propertyId}/units`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newUnit)
      });
      
      if (response.ok) {
        setShowAddUnitModal(false);
        setNewUnit({
          unit_number: '',
          unit_type_id: '',
          status: 'vacant',
          square_footage: '',
          bedrooms: '',
          bathrooms: '',
          floor_number: '',
          monthly_rent: null,
          notes: ''
        });
        fetchUnits();
        fetchPropertyData(); // Update unit count
        setSuccess('Unit created successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to create unit');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const addBulkScheme = () => {
    setBulkUnitSchemes([...bulkUnitSchemes, {
      prefix: '',
      start_number: 1,
      end_number: 1,
      unit_type_id: '',
      status: 'vacant',
      square_footage: '',
      bedrooms: '',
      bathrooms: '',
      monthly_rent: null  // Units start with null rent until priced through engine
    }]);
  };

  const removeBulkScheme = (index) => {
    if (bulkUnitSchemes.length <= 1) return;
    const updated = [...bulkUnitSchemes];
    updated.splice(index, 1);
    setBulkUnitSchemes(updated);
  };

  const updateBulkScheme = (index, field, value) => {
    const updated = [...bulkUnitSchemes];
    updated[index] = { ...updated[index], [field]: value };
    setBulkUnitSchemes(updated);
  };

  const handleBulkCreateUnits = async () => {
    // Validate all schemes
    for (const scheme of bulkUnitSchemes) {
      if (!scheme.start_number || !scheme.end_number) {
        setError('Start and end numbers are required for all schemes');
        return;
      }
      if (scheme.end_number < scheme.start_number) {
        setError('End number must be >= start number for all schemes');
        return;
      }
    }
    
    setSaving(true);
    let totalCreated = 0;
    
    try {
      // Process each scheme sequentially
      for (const scheme of bulkUnitSchemes) {
        const response = await fetch(`${API_BASE_URL}/api/properties/${propertyId}/units/bulk`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(scheme)
        });
        
        if (response.ok) {
          const data = await response.json();
          totalCreated += data.created_count;
        } else {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create some units');
        }
      }
      
      setShowBulkUnitModal(false);
      setBulkUnitSchemes([{
        prefix: '',
        start_number: 1,
        end_number: 1,
        unit_type_id: '',
        status: 'vacant',
        square_footage: '',
        bedrooms: '',
        bathrooms: '',
        monthly_rent: null  // Units start with null rent until priced through engine
      }]);
      fetchUnits();
      fetchPropertyData();
      setSuccess(`Successfully created ${totalCreated} units across all schemes!`);
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateUnit = async () => {
    if (!editingUnit) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/units/${editingUnit.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newUnit)
      });
      
      if (response.ok) {
        setShowAddUnitModal(false);
        setEditingUnit(null);
        setNewUnit({
          unit_number: '',
          unit_type_id: '',
          status: 'vacant',
          square_footage: '',
          bedrooms: '',
          bathrooms: '',
          floor_number: '',
          monthly_rent: null,
          notes: ''
        });
        fetchUnits();
        setSuccess('Unit updated successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update unit');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteUnit = async (unitId) => {
    if (!window.confirm('Are you sure you want to delete this unit?')) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/units/${unitId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        fetchUnits();
        fetchPropertyData();
        setSuccess('Unit deleted successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to delete unit');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const openEditUnitModal = (unit) => {
    setEditingUnit(unit);
    setNewUnit({
      unit_number: unit.unit_number || '',
      unit_type_id: unit.unit_type_id || '',
      status: unit.status || 'vacant',
      square_footage: unit.square_footage || '',
      bedrooms: unit.bedrooms || '',
      bathrooms: unit.bathrooms || '',
      floor_number: unit.floor_number ?? '',
      monthly_rent: unit.monthly_rent || null,
      notes: unit.notes || ''
    });
    setShowAddUnitModal(true);
  };

  // Pricing functions
  const openPricingModal = (unitTypeId, unitId = null, unitData = null) => {
    setPricingModalContext({
      unitTypeId,
      unitId,
      unitData
    });
    setShowPricingModal(true);
  };

  const handleApplyPrice = async (unitId, price, unitTypeId = null) => {
    try {
      if (unitId) {
        // Apply to single unit
        const response = await fetch(`${API_BASE_URL}/api/units/${unitId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ monthly_rent: price })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to update unit price');
        }

        setSuccess(`Price ${formatCurrency(price)} applied to unit successfully!`);
        setTimeout(() => setSuccess(''), 3000);
        fetchUnits();
      } else if (unitTypeId) {
        // Apply to all units of this type
        const unitsOfType = units.filter(u => u.unit_type_id === unitTypeId);
        const updatePromises = unitsOfType.map(unit =>
          fetch(`${API_BASE_URL}/api/units/${unit.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ monthly_rent: price })
          })
        );

        const results = await Promise.all(updatePromises);
        const failed = results.filter(r => !r.ok);

        if (failed.length > 0) {
          throw new Error(`Failed to update ${failed.length} units`);
        }

        setSuccess(`Price ${formatCurrency(price)} applied to ${unitsOfType.length} units successfully!`);
        setTimeout(() => setSuccess(''), 3000);
        fetchUnits();
      }
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const handleSave = async () => {
    if (!property) return;
    
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      // Transform UI Title Case fields back to backend snake_case before saving
      const toSnake = (key) => key.toLowerCase().replace(/[ \-]/g, '_');
      
      // Combine address fields into format: "Street, City, State zip"
      const streetAddress = (property['Street Address'] || '').trim();
      const city = (property['City'] || '').trim();
      const state = (property['State'] || '').trim();
      const zipCode = (property['Zip Code'] || '').trim();
      
      let combinedAddress = '';
      if (streetAddress || city || state || zipCode) {
        const addressParts = [];
        if (streetAddress) addressParts.push(streetAddress);
        if (city) addressParts.push(city);
        if (state && zipCode) {
          addressParts.push(`${state} ${zipCode}`);
        } else if (state) {
          addressParts.push(state);
        } else if (zipCode) {
          addressParts.push(zipCode);
        }
        combinedAddress = addressParts.join(', ');
      }
      
      const payload = {
        property_name: property['Property Name'] || '',
        address: combinedAddress,
        unit_count: Number(property['Unit Count']) || 0,
        property_manager: property['Property Manager'] || '',
        property_type: property['Property Type'] || '',
        year_built: property['Year Built'] || '',
        total_square_footage: property['Total Square Footage'] || '',
        property_status: property['Property Status'] || '',
        purchase_price: property['Purchase Price'] || '',
        current_market_value: property['Current Market Value'] || '',
        monthly_revenue: property['Monthly Revenue'] || '',
        monthly_expenses: property['Monthly Expenses'] || '',
        maintenance_contact: property['Maintenance Contact'] || '',
        maintenance_phone: property['Maintenance Phone'] || '',
        last_inspection_date: property['Last Inspection Date'] || '',
        next_inspection_date: property['Next Inspection Date'] || ''
      };
      // Include custom attributes (anything in propertyAttributes beyond the first 4 core)
      if (Array.isArray(propertyAttributes)) {
        propertyAttributes.slice(4).forEach(attr => {
          const snake = toSnake(attr);
          payload[snake] = property[attr] || '';
        });
      }

      // Use the individual property update endpoint
      const response = await fetch(`${API_BASE_URL}/api/properties/${property.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update property');
      }
      
      setSuccess('Property updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddAttribute = async () => {
    if (!newAttribute.trim()) return;
    
    setSaving(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/properties/attributes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attribute_name: newAttribute,
          attribute_type: newAttributeType
        })
      });
      
      if (!response.ok) throw new Error('Failed to add attribute');
      
      // Refresh data
      await fetchPropertyData();
      await fetchAttributeTypes();
      
      setNewAttribute('');
      setNewAttributeType('string');
      setShowAddAttributeModal(false);
      setSuccess('Attribute added successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this property? This action cannot be undone.')) {
      return;
    }
    
    setSaving(true);
    setError('');
    
    try {
      // Use the new individual property deletion endpoint
      const response = await fetch(`${API_BASE_URL}/api/properties/${property.id}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete property');
      }
      
      navigate('/databases');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value) => {
    if (isNaN(Number(value))) return value;
    const n = Number(value);
    const formatted = n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    return n < 0 ? `(${formatted.replace('-', '')})` : formatted;
  };

  // Parse a currency-formatted string back to a numeric value
  const parseCurrencyInput = (input) => {
    if (input === '' || input === null || input === undefined) return '';
    const cleaned = input.toString().replace(/\(/g, '-').replace(/\)/g, '').replace(/[^0-9.-]/g, '');
    if (cleaned === '' || cleaned === '-' || cleaned === '.' || cleaned === '-.') return '';
    const n = Number(cleaned);
    return isNaN(n) ? '' : n;
  };

  // Free-typing editing state for monetary fields to prevent cursor jumping
  const [editingMoney, setEditingMoney] = useState({});
  const startMoneyEdit = (key) => {
    setEditingMoney(prev => ({ ...prev, [key]: (property[key] ?? '').toString() }));
  };
  const changeMoneyEdit = (key, val) => {
    setEditingMoney(prev => ({ ...prev, [key]: val }));
  };
  const finishMoneyEdit = (key) => {
    setProperty({ ...property, [key]: parseCurrencyInput(editingMoney[key]) });
    setEditingMoney(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const getPropertyIcon = (propertyName) => {
    if (!propertyName) return '🏢';
    const name = propertyName.toLowerCase();
    if (name.includes('apartment')) return '🏢';
    if (name.includes('tower')) return '🏗️';
    if (name.includes('house')) return '🏠';
    if (name.includes('condo')) return '🏘️';
    if (name.includes('complex')) return '🏢';
    return '🏢';
  };

  if (loading) {
    return (
      <div className="property-edit-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="property-edit-page">
        <div className="error-message">Property not found</div>
      </div>
    );
  }

  return (
    <div className="property-edit-page">
      {/* Header */}
      <div className="property-header">
        <div className="property-profile">
          <div className="property-image">
            <div className="property-icon">
              {getPropertyIcon(property['Property Name'])}
            </div>
          </div>
          <div className="property-info">
            <h1>{property['Property Name']}</h1>
            <p className="property-subtitle">Property Profile</p>
            <p className="property-address">📍 {formatAddressForDisplay(property)}</p>
            <div className="property-stats">
              <span className="stat">
                <strong>{property['Unit Count'] || units.length}</strong> Units
              </span>
              <span className="stat">
                <strong>{dashboardMetrics.occupancyRate}%</strong> Occupied
              </span>
              <span className="stat">
                <strong>{property['Property Manager'] || 'Unassigned'}</strong> Manager
              </span>
            </div>
          </div>
        </div>
        <div className="header-actions">
          <button 
            onClick={() => navigate('/databases')} 
            className="back-button"
          >
            ← Back to Database
          </button>
          <button 
            onClick={() => navigate(`/pricing-engine?propertyId=${propertyId}`)}
            className="btn-secondary"
            style={{ marginRight: '10px' }}
          >
            💰 Set Pricing
          </button>
          <button 
            onClick={handleSave} 
            className="save-button"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
          <button 
            onClick={handleDelete} 
            className="delete-button"
            disabled={saving}
          >
            Delete Property
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* SECTION A: Property Dashboard - Key Metrics */}
      <div className="dashboard-metrics-section" style={{ margin: '20px', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
        <h2 style={{ marginTop: 0, marginBottom: '20px', fontSize: '1.5em' }}>Property Dashboard</h2>
        <div className="metrics-grid" style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '15px',
          marginBottom: '20px'
        }}>
          <div className="metric-card" style={{ 
            background: 'white', 
            padding: '20px', 
            borderRadius: '8px', 
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderLeft: '4px solid #4CAF50'
          }}>
            <div style={{ fontSize: '0.9em', color: '#666', marginBottom: '8px' }}>Scheduled Rent (This Month)</div>
            <div style={{ fontSize: '1.8em', fontWeight: 'bold', color: '#333' }}>
              {formatCurrency(dashboardMetrics.scheduledRent)}
            </div>
          </div>
          
          <div className="metric-card" style={{ 
            background: 'white', 
            padding: '20px', 
            borderRadius: '8px', 
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderLeft: '4px solid #2196F3'
          }}>
            <div style={{ fontSize: '0.9em', color: '#666', marginBottom: '8px' }}>Collected Rent</div>
            <div style={{ fontSize: '1.8em', fontWeight: 'bold', color: '#333' }}>
              {formatCurrency(dashboardMetrics.collectedRent)}
            </div>
            <div style={{ fontSize: '0.85em', color: '#4CAF50', marginTop: '5px' }}>
              {dashboardMetrics.collectionRate}% collection rate
            </div>
          </div>
          
          <div className="metric-card" style={{ 
            background: 'white', 
            padding: '20px', 
            borderRadius: '8px', 
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderLeft: '4px solid #FF9800'
          }}>
            <div style={{ fontSize: '0.9em', color: '#666', marginBottom: '8px' }}>Outstanding Rent</div>
            <div style={{ fontSize: '1.8em', fontWeight: 'bold', color: '#FF9800' }}>
              {formatCurrency(dashboardMetrics.outstandingRent)}
            </div>
          </div>
          
          <div className="metric-card" style={{ 
            background: 'white', 
            padding: '20px', 
            borderRadius: '8px', 
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderLeft: '4px solid #9C27B0'
          }}>
            <div style={{ fontSize: '0.9em', color: '#666', marginBottom: '8px' }}>Assistance Received</div>
            <div style={{ fontSize: '1.8em', fontWeight: 'bold', color: '#333' }}>
              {formatCurrency(dashboardMetrics.assistanceReceived)}
            </div>
          </div>
          
          <div className="metric-card" style={{ 
            background: 'white', 
            padding: '20px', 
            borderRadius: '8px', 
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderLeft: '4px solid #F44336'
          }}>
            <div style={{ fontSize: '0.9em', color: '#666', marginBottom: '8px' }}>Expenses (This Month)</div>
            <div style={{ fontSize: '1.8em', fontWeight: 'bold', color: '#333' }}>
              {formatCurrency(dashboardMetrics.expenses)}
            </div>
          </div>
          
          <div className="metric-card" style={{ 
            background: 'white', 
            padding: '20px', 
            borderRadius: '8px', 
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            borderLeft: '4px solid #00BCD4'
          }}>
            <div style={{ fontSize: '0.9em', color: '#666', marginBottom: '8px' }}>Net Operating Income</div>
            <div style={{ 
              fontSize: '1.8em', 
              fontWeight: 'bold', 
              color: dashboardMetrics.netOperatingIncome >= 0 ? '#4CAF50' : '#F44336'
            }}>
              {formatCurrency(dashboardMetrics.netOperatingIncome)}
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="property-tabs-nav" style={{ 
        display: 'flex', 
        gap: '4px', 
        padding: '0 20px', 
        borderBottom: '2px solid #e2e8f0',
        background: '#fff',
        marginTop: '20px'
      }}>
        <button
          className={`property-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            borderBottom: activeTab === 'overview' ? '3px solid #1457f5' : '3px solid transparent',
            color: activeTab === 'overview' ? '#1457f5' : '#64748b',
            fontWeight: activeTab === 'overview' ? 600 : 400,
            fontSize: '14px',
            transition: 'all 0.2s'
          }}
        >
          Overview
        </button>
        <button
          className={`property-tab ${activeTab === 'financial' ? 'active' : ''}`}
          onClick={() => setActiveTab('financial')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            borderBottom: activeTab === 'financial' ? '3px solid #1457f5' : '3px solid transparent',
            color: activeTab === 'financial' ? '#1457f5' : '#64748b',
            fontWeight: activeTab === 'financial' ? 600 : 400,
            fontSize: '14px',
            transition: 'all 0.2s'
          }}
        >
          Financial
        </button>
        <button
          className={`property-tab ${activeTab === 'units' ? 'active' : ''}`}
          onClick={() => setActiveTab('units')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            borderBottom: activeTab === 'units' ? '3px solid #1457f5' : '3px solid transparent',
            color: activeTab === 'units' ? '#1457f5' : '#64748b',
            fontWeight: activeTab === 'units' ? 600 : 400,
            fontSize: '14px',
            transition: 'all 0.2s'
          }}
        >
          Units
        </button>
        <button
          className={`property-tab ${activeTab === 'leases' ? 'active' : ''}`}
          onClick={() => setActiveTab('leases')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            borderBottom: activeTab === 'leases' ? '3px solid #1457f5' : '3px solid transparent',
            color: activeTab === 'leases' ? '#1457f5' : '#64748b',
            fontWeight: activeTab === 'leases' ? 600 : 400,
            fontSize: '14px',
            transition: 'all 0.2s'
          }}
        >
          Leases
        </button>
        <button
          className={`property-tab ${activeTab === 'documents' ? 'active' : ''}`}
          onClick={() => setActiveTab('documents')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            borderBottom: activeTab === 'documents' ? '3px solid #1457f5' : '3px solid transparent',
            color: activeTab === 'documents' ? '#1457f5' : '#64748b',
            fontWeight: activeTab === 'documents' ? 600 : 400,
            fontSize: '14px',
            transition: 'all 0.2s'
          }}
        >
          Documents
        </button>
        <button
          className={`property-tab ${activeTab === 'maintenance' ? 'active' : ''}`}
          onClick={() => setActiveTab('maintenance')}
          style={{
            padding: '12px 24px',
            border: 'none',
            background: 'transparent',
            cursor: 'pointer',
            borderBottom: activeTab === 'maintenance' ? '3px solid #1457f5' : '3px solid transparent',
            color: activeTab === 'maintenance' ? '#1457f5' : '#64748b',
            fontWeight: activeTab === 'maintenance' ? 600 : 400,
            fontSize: '14px',
            transition: 'all 0.2s'
          }}
        >
          Maintenance
        </button>
      </div>

      {/* Tab Content */}
      <div className="property-tab-content" style={{ padding: '20px' }}>
        {activeTab === 'overview' && (
          <div className="property-overview-tab">
            <h2 style={{ marginBottom: '20px' }}>Property Overview</h2>
            
            {/* Quick Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '30px' }}>
              <div style={{ padding: '15px', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Occupancy Rate</div>
                <div style={{ fontSize: '20px', fontWeight: 600 }}>{dashboardMetrics.occupancyRate}%</div>
              </div>
              <div style={{ padding: '15px', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Total Units</div>
                <div style={{ fontSize: '20px', fontWeight: 600 }}>{units.length || property['Unit Count'] || 0}</div>
              </div>
              <div style={{ padding: '15px', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Active Leases</div>
                <div style={{ fontSize: '20px', fontWeight: 600 }}>{leases.length}</div>
              </div>
              <div style={{ padding: '15px', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Collection Rate</div>
                <div style={{ fontSize: '20px', fontWeight: 600 }}>{dashboardMetrics.collectionRate}%</div>
              </div>
            </div>

            {/* Alerts Panel */}
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{ marginBottom: '15px' }}>Alerts & Notifications</h3>
              {alerts.length === 0 ? (
                <div style={{ padding: '20px', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center', color: '#64748b' }}>
                  No alerts at this time.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {alerts.map(alert => (
                    <div key={alert.id} style={{
                      padding: '15px',
                      background: alert.severity === 'high' ? '#fee' : alert.severity === 'medium' ? '#fff3cd' : 'white',
                      borderRadius: '8px',
                      border: `1px solid ${alert.severity === 'high' ? '#ef4444' : alert.severity === 'medium' ? '#ffc107' : '#e2e8f0'}`,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <div style={{ fontWeight: 600, marginBottom: '4px' }}>{alert.title || alert.type}</div>
                        <div style={{ fontSize: '14px', color: '#64748b' }}>{alert.message || alert.description}</div>
                        {alert.created_at && (
                          <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                            {new Date(alert.created_at).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                      {alert.severity && (
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 600,
                          background: alert.severity === 'high' ? '#ef4444' : alert.severity === 'medium' ? '#ffc107' : '#6c757d',
                          color: 'white'
                        }}>
                          {alert.severity.toUpperCase()}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Activity Log Placeholder */}
            <div style={{ marginBottom: '30px' }}>
              <h3 style={{ marginBottom: '15px' }}>Recent Activity</h3>
              <div style={{ padding: '20px', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center', color: '#64748b' }}>
                Activity log will be displayed here once implemented.
              </div>
            </div>

            {/* Property Information & Settings */}
            <div className="content-grid" style={{ marginTop: '40px', borderTop: '2px solid #e2e8f0', paddingTop: '30px' }}>
              {/* Property Information */}
              <div className="info-section">
                <h2>Property Information</h2>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Property Name *</label>
                    <input
                      type="text"
                      value={property['Property Name'] || ''}
                      onChange={(e) => setProperty({...property, 'Property Name': e.target.value})}
                      className="form-input"
                    />
                  </div>
                  <div className="form-field">
                    <label>Street Address *</label>
                    <input
                      type="text"
                      value={property['Street Address'] || ''}
                      onChange={(e) => setProperty({...property, 'Street Address': e.target.value})}
                      className="form-input"
                      placeholder="123 Main Street"
                    />
                  </div>
                  <div className="form-field">
                    <label>City *</label>
                    <input
                      type="text"
                      value={property['City'] || ''}
                      onChange={(e) => setProperty({...property, 'City': e.target.value})}
                      className="form-input"
                      placeholder="City"
                    />
                  </div>
                  <div className="form-field">
                    <label>State *</label>
                    <input
                      type="text"
                      value={property['State'] || ''}
                      onChange={(e) => setProperty({...property, 'State': e.target.value})}
                      className="form-input"
                      placeholder="State"
                      maxLength="2"
                      style={{ textTransform: 'uppercase' }}
                    />
                  </div>
                  <div className="form-field">
                    <label>Zip Code *</label>
                    <input
                      type="text"
                      value={property['Zip Code'] || ''}
                      onChange={(e) => setProperty({...property, 'Zip Code': e.target.value})}
                      className="form-input"
                      placeholder="12345"
                      maxLength="10"
                    />
                  </div>
                  <div className="form-field">
                    <label>Unit Count *</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="number"
                        value={units.length || property['Unit Count'] || ''}
                        onChange={(e) => setProperty({...property, 'Unit Count': parseInt(e.target.value) || 0})}
                        className="form-input"
                        min="0"
                        style={{ flex: 1 }}
                      />
                      <span style={{ fontSize: '12px', color: '#6c757d' }}>
                        ({units.length} units created)
                      </span>
                    </div>
                    <small style={{ color: '#6c757d', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                      This field shows the number of actual units. Create units in the Units tab to update automatically.
                    </small>
                  </div>
                  <div className="form-field">
                    <label>Property Manager *</label>
                    <input
                      type="text"
                      value={property['Property Manager'] || ''}
                      onChange={(e) => setProperty({...property, 'Property Manager': e.target.value})}
                      className="form-input"
                    />
                  </div>
                </div>
              </div>

              {/* Property Details */}
              <div className="info-section">
                <h2>Property Details</h2>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Property Type</label>
                    <select
                      value={property['Property Type'] || ''}
                      onChange={(e) => setProperty({...property, 'Property Type': e.target.value})}
                      className="form-input"
                    >
                      <option value="">Select Property Type</option>
                      <option value="Apartment Complex">Apartment Complex</option>
                      <option value="Condo Building">Condo Building</option>
                      <option value="Townhouse Community">Townhouse Community</option>
                      <option value="Single Family Homes">Single Family Homes</option>
                      <option value="Mixed Use">Mixed Use</option>
                      <option value="Commercial">Commercial</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Year Built</label>
                    <input
                      type="number"
                      value={property['Year Built'] || ''}
                      onChange={(e) => setProperty({...property, 'Year Built': e.target.value})}
                      className="form-input"
                      min="1800"
                      max={new Date().getFullYear()}
                    />
                  </div>
                  <div className="form-field">
                    <label>Total Square Footage</label>
                    <input
                      type="number"
                      value={property['Total Square Footage'] || ''}
                      onChange={(e) => setProperty({...property, 'Total Square Footage': e.target.value})}
                      className="form-input"
                      min="0"
                    />
                  </div>
                  <div className="form-field">
                    <label>Property Status</label>
                    <select
                      value={property['Property Status'] || ''}
                      onChange={(e) => setProperty({...property, 'Property Status': e.target.value})}
                      className="form-input"
                    >
                      <option value="">Select Status</option>
                      <option value="Active">Active</option>
                      <option value="Under Construction">Under Construction</option>
                      <option value="Under Renovation">Under Renovation</option>
                      <option value="For Sale">For Sale</option>
                      <option value="Sold">Sold</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Financial Information */}
              <div className="info-section">
                <h2>Financial Information</h2>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Purchase Price</label>
                    <input
                      type="text"
                      value={editingMoney['Purchase Price'] ?? (formatCurrency(property['Purchase Price']) || '')}
                      onFocus={() => startMoneyEdit('Purchase Price')}
                      onChange={(e) => changeMoneyEdit('Purchase Price', e.target.value)}
                      onBlur={() => finishMoneyEdit('Purchase Price')}
                      className="form-input"
                    />
                  </div>
                  <div className="form-field">
                    <label>Current Market Value</label>
                    <input
                      type="text"
                      value={editingMoney['Current Market Value'] ?? (formatCurrency(property['Current Market Value']) || '')}
                      onFocus={() => startMoneyEdit('Current Market Value')}
                      onChange={(e) => changeMoneyEdit('Current Market Value', e.target.value)}
                      onBlur={() => finishMoneyEdit('Current Market Value')}
                      className="form-input"
                    />
                  </div>
                  <div className="form-field">
                    <label>Monthly Revenue</label>
                    <input
                      type="text"
                      value={editingMoney['Monthly Revenue'] ?? (formatCurrency(property['Monthly Revenue']) || '')}
                      onFocus={() => startMoneyEdit('Monthly Revenue')}
                      onChange={(e) => changeMoneyEdit('Monthly Revenue', e.target.value)}
                      onBlur={() => finishMoneyEdit('Monthly Revenue')}
                      className="form-input"
                    />
                  </div>
                  <div className="form-field">
                    <label>Monthly Expenses</label>
                    <input
                      type="text"
                      value={editingMoney['Monthly Expenses'] ?? (formatCurrency(property['Monthly Expenses']) || '')}
                      onFocus={() => startMoneyEdit('Monthly Expenses')}
                      onChange={(e) => changeMoneyEdit('Monthly Expenses', e.target.value)}
                      onBlur={() => finishMoneyEdit('Monthly Expenses')}
                      className="form-input"
                    />
                  </div>
                </div>
              </div>

              {/* Owner & Financial Settings */}
              <div className="info-section">
                <h2>Owner & Financial Settings</h2>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Property Owner</label>
                    <input
                      type="text"
                      value={property['Property Owner'] || property['Owner'] || ''}
                      onChange={(e) => setProperty({...property, 'Property Owner': e.target.value, 'Owner': e.target.value})}
                      className="form-input"
                      placeholder="Owner name or company"
                    />
                  </div>
                  <div className="form-field">
                    <label>Management Fee Type</label>
                    <select
                      value={property['Management Fee Type'] || ''}
                      onChange={(e) => setProperty({...property, 'Management Fee Type': e.target.value})}
                      className="form-input"
                    >
                      <option value="">Select Fee Type</option>
                      <option value="flat_percentage">Flat Percentage</option>
                      <option value="per_unit">Per Unit</option>
                      <option value="hybrid">Hybrid</option>
                      <option value="flat_amount">Flat Amount</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Management Fee Value</label>
                    <input
                      type="text"
                      value={property['Management Fee Value'] || ''}
                      onChange={(e) => setProperty({...property, 'Management Fee Value': e.target.value})}
                      className="form-input"
                      placeholder="e.g., 10 (for 10%) or amount"
                    />
                  </div>
                  <div className="form-field">
                    <label>Operating Account</label>
                    <input
                      type="text"
                      value={property['Operating Account'] || ''}
                      onChange={(e) => setProperty({...property, 'Operating Account': e.target.value})}
                      className="form-input"
                      placeholder="Bank account identifier"
                    />
                  </div>
                  <div className="form-field">
                    <label>Trust/Escrow Account</label>
                    <input
                      type="text"
                      value={property['Trust Escrow Account'] || ''}
                      onChange={(e) => setProperty({...property, 'Trust Escrow Account': e.target.value})}
                      className="form-input"
                      placeholder="Trust/escrow account identifier"
                    />
                  </div>
                  <div className="form-field">
                    <label>Accounting Classification (LLC/EIN)</label>
                    <input
                      type="text"
                      value={property['Accounting Classification'] || ''}
                      onChange={(e) => setProperty({...property, 'Accounting Classification': e.target.value})}
                      className="form-input"
                      placeholder="LLC name or EIN"
                    />
                  </div>
                </div>
              </div>

              {/* Maintenance & Operations */}
              <div className="info-section">
                <h2>Maintenance & Operations</h2>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Maintenance Contact</label>
                    <input
                      type="text"
                      value={property['Maintenance Contact'] || ''}
                      onChange={(e) => setProperty({...property, 'Maintenance Contact': e.target.value})}
                      className="form-input"
                    />
                  </div>
                  <div className="form-field">
                    <label>Maintenance Phone</label>
                    <input
                      type="tel"
                      value={property['Maintenance Phone'] || ''}
                      onChange={(e) => setProperty({...property, 'Maintenance Phone': e.target.value})}
                      className="form-input"
                    />
                  </div>
                  <div className="form-field">
                    <label>Last Inspection Date</label>
                    <input
                      type="date"
                      value={property['Last Inspection Date'] || ''}
                      onChange={(e) => setProperty({...property, 'Last Inspection Date': e.target.value})}
                      className="form-input"
                    />
                  </div>
                  <div className="form-field">
                    <label>Next Inspection Date</label>
                    <input
                      type="date"
                      value={property['Next Inspection Date'] || ''}
                      onChange={(e) => setProperty({...property, 'Next Inspection Date': e.target.value})}
                      className="form-input"
                    />
                  </div>
                </div>
              </div>

              {/* Custom Attributes */}
              <div className="info-section">
                <div className="section-header">
                  <h2>Additional Information</h2>
                  <button 
                    onClick={() => setShowAddAttributeModal(true)}
                    className="add-attribute-button"
                  >
                    + Add Attribute
                  </button>
                </div>
                <div className="form-grid">
                  {propertyAttributes.slice(4).map(attr => {
                    const value = property[attr] || '';
                    const isCurrency = attributeTypes[attr] === 'currency' || /price|rent|revenue|expense/i.test(attr);
                    const isNumber = attributeTypes[attr] === 'number';
                    
                    return (
                      <div key={attr} className="form-field">
                        <label>{attr}</label>
                        <input
                          type={isNumber ? 'number' : 'text'}
                          value={isCurrency ? formatCurrency(value) : value}
                          onChange={(e) => setProperty({...property, [attr]: e.target.value})}
                          className="form-input"
                          placeholder={`Enter ${attr.toLowerCase()}`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Notes */}
              <div className="info-section" style={{ width: '100%', gridColumn: '1 / -1', margin: 0 }}>
                <h2>Notes</h2>
                <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
                  <div className="form-field">
                    <label>Add Note</label>
                    <textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      className="form-input"
                      rows={3}
                      placeholder="Type a note and click Add. Notes are immutable and timestamped."
                      style={{ resize: 'vertical' }}
                    />
                    <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'flex-end' }}>
                      <button className="add-attribute-button" onClick={handleAddNote} disabled={!newNote.trim() || saving}>
                        + Add Note
                      </button>
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: '12px', maxHeight: '320px', overflowY: 'auto', paddingRight: '4px' }}>
                  {notes.length === 0 ? (
                    <div style={{ color: '#6c757d' }}>No notes yet.</div>
                  ) : (
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {notes.map(n => (
                        <li key={n.id} style={{ background: '#ffffff', border: '1px solid #e9ecef', borderRadius: '10px', padding: '12px', boxShadow: '0 2px 6px rgba(0,0,0,0.05)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                            <div style={{ fontSize: '12px', color: '#6c757d' }}>{new Date(n.created_at).toLocaleString()}</div>
                            {n.created_by && <div style={{ fontSize: '12px', color: '#6c757d' }}>{n.created_by}</div>}
                          </div>
                          <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{n.note_text}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'financial' && (
          <div className="property-financial-tab">
            <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2>Financial Overview</h2>
              <button
                onClick={() => navigate(`/financial?property=${propertyId}`)}
                style={{
                  padding: '10px 20px',
                  background: '#1457f5',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500
                }}
              >
                View Full Financial Dashboard →
              </button>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
              <div style={{ padding: '20px', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>Scheduled Rent</div>
                <div style={{ fontSize: '24px', fontWeight: 600 }}>{formatCurrency(dashboardMetrics.scheduledRent)}</div>
              </div>
              <div style={{ padding: '20px', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>Collected Rent</div>
                <div style={{ fontSize: '24px', fontWeight: 600, color: '#28a745' }}>{formatCurrency(dashboardMetrics.collectedRent)}</div>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>{dashboardMetrics.collectionRate}% collection rate</div>
              </div>
              <div style={{ padding: '20px', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>Outstanding</div>
                <div style={{ fontSize: '24px', fontWeight: 600, color: '#ff9800' }}>{formatCurrency(dashboardMetrics.outstandingRent)}</div>
              </div>
              <div style={{ padding: '20px', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>Expenses</div>
                <div style={{ fontSize: '24px', fontWeight: 600 }}>{formatCurrency(dashboardMetrics.expenses)}</div>
              </div>
              <div style={{ padding: '20px', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>Net Operating Income</div>
                <div style={{ 
                  fontSize: '24px', 
                  fontWeight: 600,
                  color: dashboardMetrics.netOperatingIncome >= 0 ? '#28a745' : '#ef4444'
                }}>
                  {formatCurrency(dashboardMetrics.netOperatingIncome)}
                </div>
              </div>
            </div>

            <div style={{ padding: '20px', background: '#f8f9fa', borderRadius: '8px', textAlign: 'center', color: '#64748b' }}>
              <p>For detailed P&L statements, balance sheets, cash flows, and expense breakdowns,</p>
              <p>please use the full Financial Dashboard linked above.</p>
            </div>
          </div>
        )}

        {activeTab === 'units' && (
          <div className="property-units-tab">
            {/* Unit Mix Summary */}
            <div className="info-section" style={{ marginBottom: '30px' }}>
              <h2>Unit Mix Summary</h2>
              {unitTypes.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginTop: '15px' }}>
                  {unitTypes.map((unitType, index) => {
                    const unitsOfType = units.filter(u => u.unit_type_id === unitType.id);
                    const occupiedOfType = unitsOfType.filter(u => u.status === 'occupied').length;
                    const vacantOfType = unitsOfType.filter(u => u.status === 'vacant').length;
                    const avgRent = unitsOfType.length > 0
                      ? unitsOfType.reduce((sum, u) => sum + (parseFloat(u.monthly_rent) || 0), 0) / unitsOfType.length
                      : unitType.minimum_expected_rent || 0;
                    
                    return (
                      <div key={unitType.id || index} style={{
                        padding: '20px',
                        background: 'white',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                      }}>
                        <div style={{ fontWeight: 600, fontSize: '18px', marginBottom: '10px' }}>
                          {unitType.type_name}
                        </div>
                        <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>
                          {unitType.bedroom_count} bed / {unitType.bathroom_count} bath
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '12px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#64748b' }}>Count:</span>
                            <span style={{ fontWeight: 600 }}>{unitsOfType.length || unitType.unit_count || 0}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#64748b' }}>Occupied:</span>
                            <span style={{ fontWeight: 600, color: '#28a745' }}>{occupiedOfType}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#64748b' }}>Vacant:</span>
                            <span style={{ fontWeight: 600, color: '#6c757d' }}>{vacantOfType}</span>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', paddingTop: '8px', borderTop: '1px solid #e2e8f0' }}>
                            <span style={{ color: '#64748b' }}>Avg Rent:</span>
                            <span style={{ fontWeight: 600 }}>{formatCurrency(avgRent)}</span>
                          </div>
                        </div>
                        <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
                          <button
                            onClick={() => openPricingModal(unitType.id)}
                            style={{
                              width: '100%',
                              padding: '8px 12px',
                              background: '#1457f5',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '13px',
                              fontWeight: 500,
                              cursor: 'pointer',
                              transition: 'all 0.2s'
                            }}
                            onMouseOver={(e) => e.target.style.background = '#0d47d9'}
                            onMouseOut={(e) => e.target.style.background = '#1457f5'}
                          >
                            💰 Get Pricing
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ color: '#64748b', padding: '20px', textAlign: 'center' }}>
                  No unit types defined. Add unit types below.
                </div>
              )}
            </div>

            {/* Unit Types */}
            <div className="info-section" style={{ marginBottom: '30px' }}>
              <div className="section-header">
                <h2>Unit Types</h2>
                <button 
                  onClick={() => {
                    setEditingUnitType(null);
                    setNewUnitType({
                      type_name: '',
                      bedroom_count: 1,
                      bathroom_count: 1,
                      unit_count: 1,
                      square_footage: '',
                      minimum_expected_rent: '',
                      description: ''
                    });
                    setShowAddUnitTypeModal(true);
                  }}
                  className="add-attribute-button"
                  disabled={unitAllocation.available_units <= 0}
                >
                  + Add Unit Type
                </button>
              </div>
              
              <div className="unit-allocation-summary">
                <div className="allocation-stats">
                  <div className="allocation-stat">
                    <span className="stat-label">Total Property Units:</span>
                    <span className="stat-value">{unitAllocation.property_total_units}</span>
                  </div>
                  <div className="allocation-stat">
                    <span className="stat-label">Allocated Units:</span>
                    <span className="stat-value">{unitAllocation.total_allocated_units}</span>
                  </div>
                  <div className="allocation-stat">
                    <span className="stat-label">Available Units:</span>
                    <span className={`stat-value ${unitAllocation.available_units <= 0 ? 'no-units' : 'has-units'}`}>
                      {unitAllocation.available_units}
                    </span>
                  </div>
                </div>
                {unitAllocation.available_units <= 0 && (
                  <div className="allocation-warning">
                    <p>⚠️ All units have been allocated. You cannot add more unit types.</p>
                  </div>
                )}
              </div>
              
              {unitTypes.length === 0 ? (
                <div className="empty-state">
                  <p>No unit types defined yet. Add your first unit type to get started.</p>
                </div>
              ) : (
                <div className="unit-types-grid">
                  {unitTypes.map((unitType, index) => (
                    <div key={unitType.id || index} className="unit-type-card">
                      <div className="unit-type-header">
                        <h3>{unitType.type_name}</h3>
                        <div className="unit-type-actions">
                          <button 
                            onClick={() => openPricingModal(unitType.id)}
                            className="pricing-button"
                            style={{
                              padding: '6px 12px',
                              background: '#1457f5',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: 500,
                              cursor: 'pointer',
                              marginRight: '8px'
                            }}
                          >
                            💰 Pricing
                          </button>
                          <button 
                            onClick={() => openEditUnitTypeModal(unitType)}
                            className="edit-button"
                          >
                            Edit
                          </button>
                          <button 
                            onClick={() => handleDeleteUnitType(unitType.id)}
                            className="delete-button"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      <div className="unit-type-details">
                        <div className="unit-type-specs">
                          <span className="spec">
                            <strong>{unitType.bedroom_count}</strong> bed{unitType.bedroom_count !== 1 ? 's' : ''}
                          </span>
                          <span className="spec">
                            <strong>{unitType.bathroom_count}</strong> bath{unitType.bathroom_count !== 1 ? 's' : ''}
                          </span>
                          <span className="spec">
                            <strong>{unitType.unit_count}</strong> unit{unitType.unit_count !== 1 ? 's' : ''}
                          </span>
                        </div>
                        {unitType.square_footage && (
                          <div className="unit-type-info">
                            <span>Square Footage: {unitType.square_footage} sq ft</span>
                          </div>
                        )}
                        {unitType.minimum_expected_rent && (
                          <div className="unit-type-info">
                            <span>Minimum Expected Rent: ${unitType.minimum_expected_rent}</span>
                          </div>
                        )}
                        {unitType.description && (
                          <div className="unit-type-description">
                            <p>{unitType.description}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Units Table */}
            <div className="info-section">
              <div className="section-header">
                <h2>Units ({units.length})</h2>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button 
                    onClick={() => setShowBulkUnitModal(true)}
                    className="add-attribute-button"
                  >
                    📦 Bulk Create Units
                  </button>
                  <button 
                    onClick={() => {
                      setEditingUnit(null);
                      setNewUnit({
                        unit_number: '',
                        unit_type_id: '',
                        status: 'vacant',
                        square_footage: '',
                        bedrooms: '',
                        bathrooms: '',
                        floor_number: '',
                        monthly_rent: null,
                        notes: ''
                      });
                      setShowAddUnitModal(true);
                    }}
                    className="add-attribute-button"
                  >
                    + Add Unit
                  </button>
                </div>
              </div>
              
              {units.length === 0 ? (
                <div className="empty-state">
                  <p>No units created yet. Add units individually or use bulk create.</p>
                </div>
              ) : (
                <>
                  <div style={{ marginTop: '20px', overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                          <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Unit Number</th>
                          <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Status</th>
                          <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Tenant</th>
                          <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Bedrooms</th>
                          <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Bathrooms</th>
                          <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Rent</th>
                          <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {units.slice((unitPage - 1) * unitsPerPage, unitPage * unitsPerPage).map((unit) => (
                          <tr key={unit.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                            <td style={{ padding: '12px' }}>{unit.unit_number}</td>
                            <td style={{ padding: '12px' }}>
                              <span style={{
                                padding: '4px 12px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: 600,
                                backgroundColor: unit.status === 'occupied' ? '#d4edda' : 
                                              unit.status === 'maintenance' ? '#fff3cd' : '#e2e8f0',
                                color: unit.status === 'occupied' ? '#155724' : 
                                      unit.status === 'maintenance' ? '#856404' : '#495057'
                              }}>
                                {unit.status || 'vacant'}
                              </span>
                            </td>
                            <td style={{ padding: '12px' }}>
                              {unit.tenant_name || (unit.tenants && unit.tenants.length > 0 
                                ? unit.tenants.map(t => t.name).join(', ')
                                : '-')}
                            </td>
                            <td style={{ padding: '12px' }}>{unit.bedrooms || '-'}</td>
                            <td style={{ padding: '12px' }}>{unit.bathrooms || '-'}</td>
                            <td style={{ padding: '12px' }}>
                              {unit.monthly_rent ? `$${parseFloat(unit.monthly_rent).toLocaleString()}` : '-'}
                            </td>
                            <td style={{ padding: '12px' }}>
                              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                <button 
                                  onClick={() => openPricingModal(unit.unit_type_id, unit.id, unit)}
                                  style={{ 
                                    padding: '4px 10px', 
                                    fontSize: '12px',
                                    background: '#1457f5',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                  }}
                                  title="Get Pricing"
                                >
                                  💰
                                </button>
                                <button 
                                  onClick={() => openEditUnitModal(unit)}
                                  style={{ padding: '4px 12px', fontSize: '12px' }}
                                >
                                  Edit
                                </button>
                                <button 
                                  onClick={() => handleDeleteUnit(unit.id)}
                                  style={{ padding: '4px 12px', fontSize: '12px', color: '#dc3545' }}
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {units.length > unitsPerPage && (
                    <div className="pagination" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px', marginTop: '20px' }}>
                      <button 
                        onClick={() => setUnitPage(prev => Math.max(1, prev - 1))}
                        disabled={unitPage === 1}
                        className="btn-secondary"
                        style={{ padding: '5px 15px' }}
                      >
                        Previous
                      </button>
                      <span style={{ fontSize: '14px', color: '#64748b' }}>
                        Page {unitPage} of {Math.ceil(units.length / unitsPerPage)}
                      </span>
                      <button 
                        onClick={() => setUnitPage(prev => Math.min(Math.ceil(units.length / unitsPerPage), prev + 1))}
                        disabled={unitPage >= Math.ceil(units.length / unitsPerPage)}
                        className="btn-secondary"
                        style={{ padding: '5px 15px' }}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {activeTab === 'leases' && (
          <div className="property-leases-tab">
            <PropertyLeasesSection propertyId={propertyId} />
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="property-documents-tab">
            <PropertyDocumentsSection propertyId={propertyId} leases={leases} />
          </div>
        )}

        {activeTab === 'maintenance' && (
          <div className="property-maintenance-tab">
            <PropertyMaintenance propertyId={propertyId} />
          </div>
        )}
      </div>

      {/* Legacy Main Content - Will be removed after migration */}
      {false && (
      <div className="property-content">
        <div className="content-grid">
          {/* Basic Property Information */}
          <div className="info-section">
            <h2>Property Information</h2>
            <div className="form-grid">
              <div className="form-field">
                <label>Property Name *</label>
                <input
                  type="text"
                  value={property['Property Name'] || ''}
                  onChange={(e) => setProperty({...property, 'Property Name': e.target.value})}
                  className="form-input"
                />
              </div>
              <div className="form-field">
                <label>Street Address *</label>
                <input
                  type="text"
                  value={property['Street Address'] || ''}
                  onChange={(e) => setProperty({...property, 'Street Address': e.target.value})}
                  className="form-input"
                  placeholder="123 Main Street"
                />
              </div>
              <div className="form-field">
                <label>City *</label>
                <input
                  type="text"
                  value={property['City'] || ''}
                  onChange={(e) => setProperty({...property, 'City': e.target.value})}
                  className="form-input"
                  placeholder="City"
                />
              </div>
              <div className="form-field">
                <label>State *</label>
                <input
                  type="text"
                  value={property['State'] || ''}
                  onChange={(e) => setProperty({...property, 'State': e.target.value})}
                  className="form-input"
                  placeholder="State"
                  maxLength="2"
                  style={{ textTransform: 'uppercase' }}
                />
              </div>
              <div className="form-field">
                <label>Zip Code *</label>
                <input
                  type="text"
                  value={property['Zip Code'] || ''}
                  onChange={(e) => setProperty({...property, 'Zip Code': e.target.value})}
                  className="form-input"
                  placeholder="12345"
                  maxLength="10"
                />
              </div>
              <div className="form-field">
                <label>Unit Count *</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="number"
                    value={units.length || property['Unit Count'] || ''}
                    onChange={(e) => setProperty({...property, 'Unit Count': parseInt(e.target.value) || 0})}
                    className="form-input"
                    min="0"
                    style={{ flex: 1 }}
                  />
                  <span style={{ fontSize: '12px', color: '#6c757d' }}>
                    ({units.length} units created)
                  </span>
                </div>
                <small style={{ color: '#6c757d', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  This field shows the number of actual units. Create units below to update automatically.
                </small>
              </div>
              <div className="form-field">
                <label>Property Manager *</label>
                <input
                  type="text"
                  value={property['Property Manager'] || ''}
                  onChange={(e) => setProperty({...property, 'Property Manager': e.target.value})}
                  className="form-input"
                />
              </div>
            </div>
          </div>

          {/* Property Details */}
          <div className="info-section">
            <h2>Property Details</h2>
            <div className="form-grid">
              <div className="form-field">
                <label>Property Type</label>
                <select
                  value={property['Property Type'] || ''}
                  onChange={(e) => setProperty({...property, 'Property Type': e.target.value})}
                  className="form-input"
                >
                  <option value="">Select Property Type</option>
                  <option value="Apartment Complex">Apartment Complex</option>
                  <option value="Condo Building">Condo Building</option>
                  <option value="Townhouse Community">Townhouse Community</option>
                  <option value="Single Family Homes">Single Family Homes</option>
                  <option value="Mixed Use">Mixed Use</option>
                  <option value="Commercial">Commercial</option>
                </select>
              </div>
              <div className="form-field">
                <label>Year Built</label>
                <input
                  type="number"
                  value={property['Year Built'] || ''}
                  onChange={(e) => setProperty({...property, 'Year Built': e.target.value})}
                  className="form-input"
                  min="1800"
                  max={new Date().getFullYear()}
                />
              </div>
              <div className="form-field">
                <label>Total Square Footage</label>
                <input
                  type="number"
                  value={property['Total Square Footage'] || ''}
                  onChange={(e) => setProperty({...property, 'Total Square Footage': e.target.value})}
                  className="form-input"
                  min="0"
                />
              </div>
              <div className="form-field">
                <label>Property Status</label>
                <select
                  value={property['Property Status'] || ''}
                  onChange={(e) => setProperty({...property, 'Property Status': e.target.value})}
                  className="form-input"
                >
                  <option value="">Select Status</option>
                  <option value="Active">Active</option>
                  <option value="Under Construction">Under Construction</option>
                  <option value="Under Renovation">Under Renovation</option>
                  <option value="For Sale">For Sale</option>
                  <option value="Sold">Sold</option>
                </select>
              </div>
            </div>
          </div>

          {/* Financial Information */}
          <div className="info-section">
            <h2>Financial Information</h2>
            <div className="form-grid">
              <div className="form-field">
                <label>Purchase Price</label>
                <input
                  type="text"
                  value={editingMoney['Purchase Price'] ?? (formatCurrency(property['Purchase Price']) || '')}
                  onFocus={() => startMoneyEdit('Purchase Price')}
                  onChange={(e) => changeMoneyEdit('Purchase Price', e.target.value)}
                  onBlur={() => finishMoneyEdit('Purchase Price')}
                  className="form-input"
                />
              </div>
              <div className="form-field">
                <label>Current Market Value</label>
                <input
                  type="text"
                  value={editingMoney['Current Market Value'] ?? (formatCurrency(property['Current Market Value']) || '')}
                  onFocus={() => startMoneyEdit('Current Market Value')}
                  onChange={(e) => changeMoneyEdit('Current Market Value', e.target.value)}
                  onBlur={() => finishMoneyEdit('Current Market Value')}
                  className="form-input"
                />
              </div>
              <div className="form-field">
                <label>Monthly Revenue</label>
                <input
                  type="text"
                  value={editingMoney['Monthly Revenue'] ?? (formatCurrency(property['Monthly Revenue']) || '')}
                  onFocus={() => startMoneyEdit('Monthly Revenue')}
                  onChange={(e) => changeMoneyEdit('Monthly Revenue', e.target.value)}
                  onBlur={() => finishMoneyEdit('Monthly Revenue')}
                  className="form-input"
                />
              </div>
              <div className="form-field">
                <label>Monthly Expenses</label>
                <input
                  type="text"
                  value={editingMoney['Monthly Expenses'] ?? (formatCurrency(property['Monthly Expenses']) || '')}
                  onFocus={() => startMoneyEdit('Monthly Expenses')}
                  onChange={(e) => changeMoneyEdit('Monthly Expenses', e.target.value)}
                  onBlur={() => finishMoneyEdit('Monthly Expenses')}
                  className="form-input"
                />
              </div>
            </div>
          </div>

          {/* Maintenance & Operations */}
          <div className="info-section">
            <h2>Maintenance & Operations</h2>
            <div className="form-grid">
              <div className="form-field">
                <label>Maintenance Contact</label>
                <input
                  type="text"
                  value={property['Maintenance Contact'] || ''}
                  onChange={(e) => setProperty({...property, 'Maintenance Contact': e.target.value})}
                  className="form-input"
                />
              </div>
              <div className="form-field">
                <label>Maintenance Phone</label>
                <input
                  type="tel"
                  value={property['Maintenance Phone'] || ''}
                  onChange={(e) => setProperty({...property, 'Maintenance Phone': e.target.value})}
                  className="form-input"
                />
              </div>
              <div className="form-field">
                <label>Last Inspection Date</label>
                <input
                  type="date"
                  value={property['Last Inspection Date'] || ''}
                  onChange={(e) => setProperty({...property, 'Last Inspection Date': e.target.value})}
                  className="form-input"
                />
              </div>
              <div className="form-field">
                <label>Next Inspection Date</label>
                <input
                  type="date"
                  value={property['Next Inspection Date'] || ''}
                  onChange={(e) => setProperty({...property, 'Next Inspection Date': e.target.value})}
                  className="form-input"
                />
              </div>
            </div>
          </div>

          {/* Custom Attributes */}
          <div className="info-section">
            <div className="section-header">
              <h2>Additional Information</h2>
              <button 
                onClick={() => setShowAddAttributeModal(true)}
                className="add-attribute-button"
              >
                + Add Attribute
              </button>
            </div>
            <div className="form-grid">
              {propertyAttributes.slice(4).map(attr => {
                const value = property[attr] || '';
                const isCurrency = attributeTypes[attr] === 'currency' || /price|rent|revenue|expense/i.test(attr);
                const isNumber = attributeTypes[attr] === 'number';
                
                return (
                  <div key={attr} className="form-field">
                    <label>{attr}</label>
                    <input
                      type={isNumber ? 'number' : 'text'}
                      value={isCurrency ? formatCurrency(value) : value}
                      onChange={(e) => setProperty({...property, [attr]: e.target.value})}
                      className="form-input"
                      placeholder={`Enter ${attr.toLowerCase()}`}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Unit Types */}
          <div className="info-section">
            <div className="section-header">
              <h2>Unit Types</h2>
              <button 
                onClick={() => {
                  setEditingUnitType(null);
                  setNewUnitType({
                    type_name: '',
                    bedroom_count: 1,
                    bathroom_count: 1,
                    unit_count: 1,
                    square_footage: '',
                    minimum_expected_rent: '',
                    description: ''
                  });
                  setShowAddUnitTypeModal(true);
                }}
                className="add-attribute-button"
                disabled={unitAllocation.available_units <= 0}
              >
                + Add Unit Type
              </button>
            </div>
            
            {/* Unit Allocation Summary */}
            <div className="unit-allocation-summary">
              <div className="allocation-stats">
                <div className="allocation-stat">
                  <span className="stat-label">Total Property Units:</span>
                  <span className="stat-value">{unitAllocation.property_total_units}</span>
                </div>
                <div className="allocation-stat">
                  <span className="stat-label">Allocated Units:</span>
                  <span className="stat-value">{unitAllocation.total_allocated_units}</span>
                </div>
                <div className="allocation-stat">
                  <span className="stat-label">Available Units:</span>
                  <span className={`stat-value ${unitAllocation.available_units <= 0 ? 'no-units' : 'has-units'}`}>
                    {unitAllocation.available_units}
                  </span>
                </div>
              </div>
              {unitAllocation.available_units <= 0 && (
                <div className="allocation-warning">
                  <p>⚠️ All units have been allocated. You cannot add more unit types.</p>
                </div>
              )}
            </div>
            
            {unitTypes.length === 0 ? (
              <div className="empty-state">
                <p>No unit types defined yet. Add your first unit type to get started.</p>
              </div>
            ) : (
              <div className="unit-types-grid">
                {unitTypes.map((unitType, index) => (
                  <div key={unitType.id || index} className="unit-type-card">
                    <div className="unit-type-header">
                      <h3>{unitType.type_name}</h3>
                      <div className="unit-type-actions">
                        <button 
                          onClick={() => openEditUnitTypeModal(unitType)}
                          className="edit-button"
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => handleDeleteUnitType(unitType.id)}
                          className="delete-button"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    <div className="unit-type-details">
                      <div className="unit-type-specs">
                        <span className="spec">
                          <strong>{unitType.bedroom_count}</strong> bed{unitType.bedroom_count !== 1 ? 's' : ''}
                        </span>
                        <span className="spec">
                          <strong>{unitType.bathroom_count}</strong> bath{unitType.bathroom_count !== 1 ? 's' : ''}
                        </span>
                        <span className="spec">
                          <strong>{unitType.unit_count}</strong> unit{unitType.unit_count !== 1 ? 's' : ''}
                        </span>
                      </div>
                      {unitType.square_footage && (
                        <div className="unit-type-info">
                          <span>Square Footage: {unitType.square_footage} sq ft</span>
                        </div>
                      )}
                      {unitType.base_rent && (
                        <div className="unit-type-info">
                          <span>Base Rent: ${unitType.base_rent}</span>
                        </div>
                      )}
                      {unitType.description && (
                        <div className="unit-type-description">
                          <p>{unitType.description}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Units */}
          <div className="info-section" style={{ width: '100%', gridColumn: '1 / -1', margin: 0 }}>
            <div className="section-header">
              <h2>Units ({units.length})</h2>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button 
                  onClick={() => setShowBulkUnitModal(true)}
                  className="add-attribute-button"
                >
                  📦 Bulk Create Units
                </button>
                <button 
                  onClick={() => {
                    setEditingUnit(null);
                    setNewUnit({
                      unit_number: '',
                      unit_type_id: '',
                      status: 'vacant',
                      square_footage: '',
                      bedrooms: '',
                      bathrooms: '',
                      floor_number: '',
                      monthly_rent: null,
                      notes: ''
                    });
                    setShowAddUnitModal(true);
                  }}
                  className="add-attribute-button"
                >
                  + Add Unit
                </button>
              </div>
            </div>
            
            {units.length === 0 ? (
              <div className="empty-state">
                <p>No units created yet. Add units individually or use bulk create.</p>
              </div>
            ) : (
              <>
                <div style={{ marginTop: '20px', overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Unit Number</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Status</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Tenant</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Bedrooms</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Bathrooms</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Rent</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {units.slice((unitPage - 1) * unitsPerPage, unitPage * unitsPerPage).map((unit) => (
                        <tr key={unit.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                          <td style={{ padding: '12px' }}>{unit.unit_number}</td>
                          <td style={{ padding: '12px' }}>
                            <span style={{
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: 600,
                              backgroundColor: unit.status === 'occupied' ? '#d4edda' : 
                                            unit.status === 'maintenance' ? '#fff3cd' : '#e2e8f0',
                              color: unit.status === 'occupied' ? '#155724' : 
                                    unit.status === 'maintenance' ? '#856404' : '#495057'
                            }}>
                              {unit.status || 'vacant'}
                            </span>
                          </td>
                          <td style={{ padding: '12px' }}>
                            {unit.tenant_name || (unit.tenants && unit.tenants.length > 0 
                              ? unit.tenants.map(t => t.name).join(', ')
                              : '-')}
                          </td>
                          <td style={{ padding: '12px' }}>{unit.bedrooms || '-'}</td>
                          <td style={{ padding: '12px' }}>{unit.bathrooms || '-'}</td>
                          <td style={{ padding: '12px' }}>
                            {unit.monthly_rent ? `$${parseFloat(unit.monthly_rent).toLocaleString()}` : '-'}
                          </td>
                          <td style={{ padding: '12px' }}>
                            <button 
                              onClick={() => openEditUnitModal(unit)}
                              style={{ marginRight: '8px', padding: '4px 12px', fontSize: '12px' }}
                            >
                              Edit
                            </button>
                            <button 
                              onClick={() => handleDeleteUnit(unit.id)}
                              style={{ padding: '4px 12px', fontSize: '12px', color: '#dc3545' }}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {units.length > unitsPerPage && (
                  <div className="pagination" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px', marginTop: '20px' }}>
                    <button 
                      onClick={() => setUnitPage(prev => Math.max(1, prev - 1))}
                      disabled={unitPage === 1}
                      className="btn-secondary"
                      style={{ padding: '5px 15px' }}
                    >
                      Previous
                    </button>
                    <span style={{ fontSize: '14px', color: '#64748b' }}>
                      Page {unitPage} of {Math.ceil(units.length / unitsPerPage)}
                    </span>
                    <button 
                      onClick={() => setUnitPage(prev => Math.min(Math.ceil(units.length / unitsPerPage), prev + 1))}
                      disabled={unitPage >= Math.ceil(units.length / unitsPerPage)}
                      className="btn-secondary"
                      style={{ padding: '5px 15px' }}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Notes */}
          <div className="info-section" style={{ width: '100%', gridColumn: '1 / -1', margin: 0 }}>
            <h2>Notes</h2>
            <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
              <div className="form-field">
                <label>Add Note</label>
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  className="form-input"
                  rows={3}
                  placeholder="Type a note and click Add. Notes are immutable and timestamped."
                  style={{ resize: 'vertical' }}
                />
                <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'flex-end' }}>
                  <button className="add-attribute-button" onClick={handleAddNote} disabled={!newNote.trim() || saving}>
                    + Add Note
                  </button>
                </div>
              </div>
            </div>
            <div style={{ marginTop: '12px', maxHeight: '320px', overflowY: 'auto', paddingRight: '4px' }}>
              {notes.length === 0 ? (
                <div style={{ color: '#6c757d' }}>No notes yet.</div>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {notes.map(n => (
                    <li key={n.id} style={{ background: '#ffffff', border: '1px solid #e9ecef', borderRadius: '10px', padding: '12px', boxShadow: '0 2px 6px rgba(0,0,0,0.05)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                        <div style={{ fontSize: '12px', color: '#6c757d' }}>{new Date(n.created_at).toLocaleString()}</div>
                        {n.created_by && <div style={{ fontSize: '12px', color: '#6c757d' }}>{n.created_by}</div>}
                      </div>
                      <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{n.note_text}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Maintenance Requests */}
          <PropertyMaintenance propertyId={propertyId} />
        </div>
      </div>
      )}

      {/* Add Attribute Modal */}
      {showAddAttributeModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Add New Attribute</h2>
            <div className="form-field">
              <label>Attribute Name</label>
              <input
                type="text"
                value={newAttribute}
                onChange={(e) => setNewAttribute(e.target.value)}
                className="form-input"
                placeholder="e.g., Year Built, Monthly Revenue, Maintenance Contact"
              />
            </div>
            <div className="form-field">
              <label>Data Type</label>
              <select
                value={newAttributeType}
                onChange={(e) => setNewAttributeType(e.target.value)}
                className="form-input"
              >
                <option value="string">Text</option>
                <option value="number">Number</option>
                <option value="currency">Currency</option>
              </select>
            </div>
            <div className="modal-actions">
              <button 
                onClick={handleAddAttribute}
                className="save-button"
                disabled={saving || !newAttribute.trim()}
              >
                {saving ? 'Adding...' : 'Add Attribute'}
              </button>
              <button 
                onClick={() => setShowAddAttributeModal(false)}
                className="cancel-button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Unit Type Modal */}
      {showAddUnitTypeModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>{editingUnitType ? 'Edit Unit Type' : 'Add New Unit Type'}</h2>
            <div className="form-grid">
              <div className="form-field">
                <label>Type Name *</label>
                <input
                  type="text"
                  value={newUnitType.type_name}
                  onChange={(e) => setNewUnitType({...newUnitType, type_name: e.target.value})}
                  className="form-input"
                  placeholder="e.g., Studio, 1BR, 2BR Deluxe"
                />
              </div>
              <div className="form-field">
                <label>Bedrooms *</label>
                <input
                  type="number"
                  value={newUnitType.bedroom_count}
                  onChange={(e) => setNewUnitType({...newUnitType, bedroom_count: parseInt(e.target.value) || 0})}
                  className="form-input"
                  min="0"
                />
              </div>
              <div className="form-field">
                <label>Bathrooms *</label>
                <input
                  type="number"
                  value={newUnitType.bathroom_count}
                  onChange={(e) => setNewUnitType({...newUnitType, bathroom_count: parseFloat(e.target.value) || 0})}
                  className="form-input"
                  min="0"
                  step="0.5"
                />
              </div>
                             <div className="form-field">
                 <label>Number of Units *</label>
                 <div className="unit-count-input">
                   <input
                     type="number"
                     value={newUnitType.unit_count}
                     onChange={(e) => setNewUnitType({...newUnitType, unit_count: parseInt(e.target.value) || 0})}
                     className="form-input"
                     min="1"
                     max={editingUnitType ? 
                       unitAllocation.available_units + (editingUnitType.unit_count || 0) : 
                       unitAllocation.available_units
                     }
                   />
                   <span className="available-units-hint">
                     Max: {editingUnitType ? 
                       unitAllocation.available_units + (editingUnitType.unit_count || 0) : 
                       unitAllocation.available_units
                     } units available
                   </span>
                 </div>
               </div>
              <div className="form-field">
                <label>Square Footage</label>
                <input
                  type="number"
                  value={newUnitType.square_footage}
                  onChange={(e) => setNewUnitType({...newUnitType, square_footage: e.target.value})}
                  className="form-input"
                  min="0"
                  placeholder="e.g., 750"
                />
              </div>
              <div className="form-field">
                <label>Base rent (required) *</label>
                <input
                  type="number"
                  value={newUnitType.minimum_expected_rent}
                  onChange={(e) => setNewUnitType({...newUnitType, minimum_expected_rent: e.target.value})}
                  className="form-input"
                  min="0"
                  step="0.01"
                  placeholder="e.g., 1200"
                  required
                />
              </div>
              <div className="form-field">
                <label>Floor rent premium ($/floor above 1)</label>
                <input
                  type="number"
                  value={newUnitType.floor_rent_premium}
                  onChange={(e) => setNewUnitType({...newUnitType, floor_rent_premium: e.target.value})}
                  className="form-input"
                  min="0"
                  step="1"
                  placeholder="e.g., 25"
                />
                <span className="form-hint">Optional. Added per floor above 1 (e.g. +$25 for floor 2)</span>
              </div>
            </div>
            <div className="form-field">
              <label>Description</label>
              <textarea
                value={newUnitType.description}
                onChange={(e) => setNewUnitType({...newUnitType, description: e.target.value})}
                className="form-input"
                placeholder="Optional description of this unit type..."
                rows="3"
              />
            </div>
            <div className="modal-actions">
              <button 
                onClick={editingUnitType ? handleUpdateUnitType : handleAddUnitType}
                className="save-button"
                disabled={saving || !newUnitType.type_name.trim() || (!editingUnitType && (newUnitType.minimum_expected_rent === '' || parseFloat(newUnitType.minimum_expected_rent) < 0))}
              >
                {saving ? 'Saving...' : (editingUnitType ? 'Update Unit Type' : 'Add Unit Type')}
              </button>
              <button 
                onClick={() => {
                  setShowAddUnitTypeModal(false);
                  setEditingUnitType(null);
                  // Reset form to default values
                  setNewUnitType({
                    type_name: '',
                    bedroom_count: 1,
                    bathroom_count: 1,
                    unit_count: 1,
                    square_footage: '',
                    minimum_expected_rent: '',
                    description: ''
                  });
                }}
                className="cancel-button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

        {/* Add/Edit Unit Modal */}
        {showAddUnitModal && (
          <div className="modal-overlay" onClick={() => setShowAddUnitModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
              <div className="modal-header">
                <h2>{editingUnit ? 'Edit Unit' : 'Add Unit'}</h2>
                <button className="modal-close" onClick={() => setShowAddUnitModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-field">
                    <label>Unit Number *</label>
                    <input
                      type="text"
                      value={newUnit.unit_number}
                      onChange={(e) => setNewUnit({...newUnit, unit_number: e.target.value})}
                      className="form-input"
                      placeholder="e.g., 101, A-5, Unit 3"
                    />
                  </div>
                  <div className="form-field">
                    <label>Unit Type</label>
                    <select
                      value={newUnit.unit_type_id || ''}
                      onChange={(e) => setNewUnit({...newUnit, unit_type_id: e.target.value || null})}
                      className="form-input"
                    >
                      <option value="">None</option>
                      {unitTypes.map(ut => (
                        <option key={ut.id} value={ut.id}>{ut.type_name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Status</label>
                    <select
                      value={newUnit.status}
                      onChange={(e) => setNewUnit({...newUnit, status: e.target.value})}
                      className="form-input"
                    >
                      <option value="vacant">Vacant</option>
                      <option value="occupied">Occupied</option>
                      <option value="maintenance">Maintenance</option>
                      <option value="reserved">Reserved</option>
                    </select>
                  </div>
                  <div className="form-field">
                    <label>Bedrooms</label>
                    <input
                      type="number"
                      value={newUnit.bedrooms}
                      onChange={(e) => setNewUnit({...newUnit, bedrooms: e.target.value})}
                      className="form-input"
                      min="0"
                    />
                  </div>
                  <div className="form-field">
                    <label>Bathrooms</label>
                    <input
                      type="number"
                      value={newUnit.bathrooms}
                      onChange={(e) => setNewUnit({...newUnit, bathrooms: e.target.value})}
                      className="form-input"
                      min="0"
                      step="0.5"
                    />
                  </div>
                  <div className="form-field">
                    <label>Monthly Rent</label>
                    <input
                      type="number"
                      value={newUnit.monthly_rent || ''}
                      onChange={(e) => setNewUnit({...newUnit, monthly_rent: e.target.value ? parseFloat(e.target.value) : null})}
                      placeholder="Set via pricing engine"
                      className="form-input"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className="form-field">
                    <label>Square Footage</label>
                    <input
                      type="number"
                      value={newUnit.square_footage}
                      onChange={(e) => setNewUnit({...newUnit, square_footage: e.target.value})}
                      className="form-input"
                      min="0"
                    />
                  </div>
                  <div className="form-field">
                    <label>Floor number</label>
                    <input
                      type="number"
                      value={newUnit.floor_number}
                      onChange={(e) => setNewUnit({...newUnit, floor_number: e.target.value})}
                      className="form-input"
                      min="1"
                      placeholder="1"
                    />
                    <span className="form-hint">Used with unit type to compute rent (base + premium per floor above 1)</span>
                  </div>
                </div>
                {newUnit.unit_type_id && (() => {
                  const ut = unitTypes.find(t => String(t.id) === String(newUnit.unit_type_id));
                  if (!ut) return null;
                  const base = parseFloat(ut.minimum_expected_rent) || 0;
                  const premium = parseFloat(ut.floor_rent_premium) || 0;
                  const floor = parseInt(newUnit.floor_number, 10) || 1;
                  const computed = base + (floor - 1) * premium;
                  return (
                    <div className="form-field" style={{ marginBottom: '12px' }}>
                      <strong>Computed rent:</strong> ${computed.toFixed(2)}
                      {premium > 0 && floor > 1 && (
                        <span className="form-hint" style={{ display: 'block', marginTop: '4px' }}>
                          (base ${base.toFixed(2)} + {(floor - 1)} × ${premium.toFixed(2)}/floor)
                        </span>
                      )}
                    </div>
                  );
                })()}
                <div className="form-field">
                  <label>Notes</label>
                  <textarea
                    value={newUnit.notes}
                    onChange={(e) => setNewUnit({...newUnit, notes: e.target.value})}
                    className="form-input"
                    rows="3"
                    placeholder="Optional notes about this unit..."
                  />
                </div>
                <div className="modal-actions">
                  <button 
                    onClick={editingUnit ? handleUpdateUnit : handleCreateUnit}
                    className="save-button"
                    disabled={saving || !newUnit.unit_number.trim()}
                  >
                    {saving ? 'Saving...' : (editingUnit ? 'Update Unit' : 'Add Unit')}
                  </button>
                  <button 
                    onClick={() => {
                      setShowAddUnitModal(false);
                      setEditingUnit(null);
                      setNewUnit({
                        unit_number: '',
                        unit_type_id: '',
                        status: 'vacant',
                        square_footage: '',
                        bedrooms: '',
                        bathrooms: '',
                        floor_number: '',
                        monthly_rent: null,
                        notes: ''
                      });
                    }}
                    className="cancel-button"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Create Units Modal */}
        {showBulkUnitModal && (
          <div className="modal-overlay" onClick={() => setShowBulkUnitModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', width: '90%' }}>
              <div className="modal-header">
                <h2>Bulk Create Units</h2>
                <button className="modal-close" onClick={() => setShowBulkUnitModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: '#e7f3ff', borderRadius: '8px', fontSize: '14px' }}>
                  <strong>Numbering Schemes:</strong> Define one or more ranges of units to create.
                  <br />Example: Prefix "A" with range 101-110 will create A101, A102... A110.
                </div>
                
                <div className="schemes-container" style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '20px' }}>
                  {bulkUnitSchemes.map((scheme, index) => (
                    <div key={index} className="scheme-row" style={{ 
                      padding: '15px', 
                      border: '1px solid #e2e8f0', 
                      borderRadius: '8px', 
                      marginBottom: '15px',
                      position: 'relative',
                      backgroundColor: '#f8fafc'
                    }}>
                      {bulkUnitSchemes.length > 1 && (
                        <button 
                          className="remove-scheme" 
                          onClick={() => removeBulkScheme(index)}
                          style={{
                            position: 'absolute',
                            top: '10px',
                            right: '10px',
                            background: 'none',
                            border: 'none',
                            color: '#ef4444',
                            fontSize: '20px',
                            cursor: 'pointer'
                          }}
                        >
                          ×
                        </button>
                      )}
                      <h4 style={{ marginTop: 0, marginBottom: '15px' }}>Scheme #{index + 1}</h4>
                      
                      <div className="form-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
                        <div className="form-field">
                          <label>Prefix</label>
                          <input
                            type="text"
                            value={scheme.prefix}
                            onChange={(e) => updateBulkScheme(index, 'prefix', e.target.value)}
                            className="form-input"
                            placeholder="e.g., A"
                          />
                        </div>
                        <div className="form-field">
                          <label>Start *</label>
                          <input
                            type="number"
                            value={scheme.start_number}
                            onChange={(e) => updateBulkScheme(index, 'start_number', parseInt(e.target.value) || 1)}
                            className="form-input"
                            min="1"
                          />
                        </div>
                        <div className="form-field">
                          <label>End *</label>
                          <input
                            type="number"
                            value={scheme.end_number}
                            onChange={(e) => updateBulkScheme(index, 'end_number', parseInt(e.target.value) || 1)}
                            className="form-input"
                            min="1"
                          />
                        </div>
                        <div className="form-field">
                          <label>Unit Type</label>
                          <select
                            value={scheme.unit_type_id || ''}
                            onChange={(e) => updateBulkScheme(index, 'unit_type_id', e.target.value || null)}
                            className="form-input"
                          >
                            <option value="">None</option>
                            {unitTypes.map(ut => (
                              <option key={ut.id} value={ut.id}>{ut.type_name}</option>
                            ))}
                          </select>
                        </div>
                        <div className="form-field">
                          <label>Monthly Rent</label>
                          <input
                            type="number"
                            value={scheme.monthly_rent || ''}
                            onChange={(e) => updateBulkScheme(index, 'monthly_rent', e.target.value ? parseFloat(e.target.value) : null)}
                            className="form-input"
                            placeholder="Leave blank - set via pricing engine"
                          />
                        </div>
                        <div className="form-field">
                          <label>Bedrooms</label>
                          <input
                            type="number"
                            value={scheme.bedrooms}
                            onChange={(e) => updateBulkScheme(index, 'bedrooms', e.target.value)}
                            className="form-input"
                          />
                        </div>
                        <div className="form-field">
                          <label>Bathrooms</label>
                          <input
                            type="number"
                            value={scheme.bathrooms}
                            onChange={(e) => updateBulkScheme(index, 'bathrooms', e.target.value)}
                            className="form-input"
                            step="0.5"
                          />
                        </div>
                        <div className="form-field">
                          <label>Sq Footage</label>
                          <input
                            type="number"
                            value={scheme.square_footage}
                            onChange={(e) => updateBulkScheme(index, 'square_footage', e.target.value)}
                            className="form-input"
                          />
                        </div>
                      </div>
                      <div style={{ marginTop: '10px', fontSize: '12px', color: '#64748b' }}>
                        {scheme.end_number >= scheme.start_number 
                          ? `Will create ${scheme.end_number - scheme.start_number + 1} units (e.g. ${scheme.prefix}${scheme.start_number} to ${scheme.prefix}${scheme.end_number})`
                          : 'End number must be >= start number'}
                      </div>
                    </div>
                  ))}
                </div>

                <button 
                  type="button" 
                  onClick={addBulkScheme}
                  className="btn-secondary"
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    marginBottom: '20px',
                    width: '100%',
                    justifyContent: 'center',
                    padding: '10px',
                    border: '2px dashed #cbd5e1',
                    backgroundColor: 'transparent',
                    color: '#64748b'
                  }}
                >
                  <span style={{ fontSize: '20px' }}>+</span> Add Another Scheme
                </button>

                <div className="modal-actions">
                  <button 
                    onClick={handleBulkCreateUnits}
                    className="save-button"
                    disabled={saving}
                  >
                    {saving ? 'Creating...' : `Create All Units`}
                  </button>
                  <button 
                    onClick={() => {
                      setShowBulkUnitModal(false);
                      setBulkUnitSchemes([{
                        prefix: '',
                        start_number: 1,
                        end_number: 1,
                        unit_type_id: '',
                        status: 'vacant',
                        square_footage: '',
                        bedrooms: '',
                        bathrooms: '',
                        monthly_rent: null  // Units start with null rent until priced through engine
                      }]);
                    }}
                    className="cancel-button"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Pricing Modal */}
        <QuickPricingModal
          isOpen={showPricingModal}
          onClose={() => {
            setShowPricingModal(false);
            setPricingModalContext({ unitTypeId: null, unitId: null, unitData: null });
          }}
          propertyId={propertyId ? parseInt(propertyId) : null}
          unitTypeId={pricingModalContext.unitTypeId}
          unitId={pricingModalContext.unitId}
          unitData={pricingModalContext.unitData}
          onApplyPrice={handleApplyPrice}
        />
    </div>
  );
}

export default PropertyEditPage; 